// src/encounter/rail-v3/rail-v3-model.ts
// The non-visual rules of the restyled left rail (LEFT-PANEL.md @70c6624b): section order and titles (§2), the empty copy (§2),
// which sections start folded (§2) and what each row puts at its end (§4). Components live in `rail-v3-column.tsx`.
import type { IpsAction, IpsEntry, IpsSection } from '@scripsteam/scrips-signal-ds';
import type { ChartColumnSection, ChartRowExtras } from '../lib/chart-rail-types';
import { isRailV3Action } from './rail-v3-actions';

/** The rail's order = ELATION's Clinical Profile order (left-rail target 4 Nov 2026 §1, Samer's second message: "Elation order …,
 *  Antonin UI design"; the catalogue's own order is NOT a source for order). Contacts (Elation's demographics block) come first, drawn
 *  before these by the column (`RAIL_V3_CONTACTS_FIRST`); then Flags (Elation's Tags), Allergies, the Problem list with the Resolved
 *  problems under it, Elation's History (PSH, FH, SH, Occupation, Education), Medications, Vaccinations (Health Maintenance).
 *  "Under workup" has no data source in PA, so it is not listed (never invented). A section the rail has that is not listed keeps
 *  its place at the end. */
export const RAIL_V3_ORDER: readonly string[] = [
  'flags',
  'allergies',
  'active-problems',
  'resolved-problems',
  'procedures',
  'family-history',
  'social-history',
  'occupation',
  'education',
  'medications',
  'vaccinations',
];

/** Contacts (today's `RailV3Contacts`) lead the rail, as Elation's demographics block sits at the top of its sidebar (§1, item 1).
 *  Not a clinical section: it keeps its fold. */
export const RAIL_V3_NON_CLINICAL: readonly string[] = ['contacts'];

/** The device-data card's slot on the started visit: right after Flags (30 Oct ruling; §4b FINAL overrides §1's "after
 *  Vaccinations"), at the top of the clinical sections when there is no Flags section. */
export const RAIL_V3_DEVICE_DATA_AFTER: readonly string[] = ['flags'];

/** The sections in the rail's order: the clinical ones in `RAIL_V3_ORDER`, then any section it does not list, in the rail's own order. */
export function railV3OrderSections<T extends { section: { id: string } }>(sections: readonly T[]): T[] {
  const byId = new Map(sections.map((m) => [m.section.id, m]));
  return [
    ...RAIL_V3_ORDER.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : [])),
    ...sections.filter((m) => !RAIL_V3_ORDER.includes(m.section.id)),
  ];
}

/** Where the device data goes in `ordered`: after the last of `RAIL_V3_DEVICE_DATA_AFTER` present (0 when none is). */
export function railV3DeviceDataAt(ordered: readonly { section: { id: string } }[]): number {
  return ordered.reduce((at, m, i) => (RAIL_V3_DEVICE_DATA_AFTER.includes(m.section.id) ? i + 1 : at), 0);
}

/** Sentence-case titles (§2). Other sections keep the rail's own title. */
export const RAIL_V3_TITLE: Readonly<Record<string, string>> = {
  allergies: 'Allergies',
  'active-problems': 'Problem list',
  'resolved-problems': 'Resolved problems',
  medications: 'Medications',
  flags: 'Flags',
  procedures: 'Past surgical history',
  vaccinations: 'Vaccinations',
  'family-history': 'Family history',
  'social-history': 'Social history',
  occupation: 'Occupation',
  education: 'Education',
};

/** The empty line describes the record, never the patient (§2). The designer's exact copy, per section (the Signal
 *  catalog's PatientContextRail EMPTY map, feat/signal@70c6624b). */
const RAIL_V3_EMPTY: Readonly<Record<string, string>> = {
  allergies: 'No allergies recorded',
  'active-problems': 'No active problems on the record',
  'resolved-problems': 'No resolved problems on the record',
  medications: 'None on the list',
  flags: 'No flags recorded',
  procedures: 'No past surgical history recorded',
  vaccinations: 'No vaccinations recorded',
  'family-history': 'No family history recorded',
  'social-history': 'No social history recorded',
  occupation: 'No occupation recorded',
  education: 'No education recorded',
};

/** The section "+" tooltips the left-rail target rules (§4b FINAL: Medications "+" = "Document medication the patient is on"). Every
 *  other section keeps the rail's own label. */
const RAIL_V3_ADD_LABEL: Readonly<Record<string, string>> = {
  medications: 'Document medication the patient is on',
};

export function railV3AddLabel(sectionId: string): string | undefined {
  return RAIL_V3_ADD_LABEL[sectionId];
}

export function railV3EmptyCopy(sectionId: string): string {
  return RAIL_V3_EMPTY[sectionId] ?? 'None on the list';
}

export function railV3Title(section: IpsSection): string {
  return RAIL_V3_TITLE[section.id] ?? section.title;
}

/** Sections that start folded even with entries on them (§1: Occupation and Education, folded; and the Contacts, which are not a
 *  clinical section). Tags, Appointments and Care team are not in PA's rail. */
const STARTS_FOLDED: readonly string[] = ['occupation', 'education', ...RAIL_V3_NON_CLINICAL];

/** Open on load (LEFT-PANEL.md @f28d00c1, Samer 2 Nov): every clinical section is open on every page, even when it is
 *  empty (its "No ... recorded" line shows) or was not read (its "unavailable" line shows); only the non-clinical
 *  sections PA has (Occupation, Education) start folded. */
export function railV3DefaultOpen(sectionId: string, unavailable?: boolean): boolean {
  if (unavailable) return true;
  return !STARTS_FOLDED.includes(sectionId);
}

/** The count follows the title only when there is something to count: not on an empty section, not on one that was not read. */
export function railV3ShowCount(entryCount: number, unavailable?: boolean): boolean {
  return !unavailable && entryCount > 0;
}


export interface RailV3Status {
  word: string;
  /** Red only when it means something clinically. */
  alert: boolean;
}

export interface RailV3RowParts {
  /** Numbers at the row's end, right-aligned (§4), joined by " · ". */
  meta?: string;
  /** Words that stay inline after the name (family and social history, and other free text). */
  inline?: string;
  /** Glyph plus word, at the very end. */
  status?: RailV3Status;
}

/** The end slot is for short codes, years, dates and doses (board: "2019 · I10", "500 mg"). Longer text stays inline after the name. */
const META_MAX = 16;

/** Split text pieces into the end slot (short) and the inline text (long), keeping their order. */
function splitMeta(...pieces: (string | number | undefined)[]): { meta?: string; inline?: string } {
  const all = pieces.filter((x) => x !== undefined && x !== '').map(String);
  const meta = all.filter((x) => x.length <= META_MAX).join(' · ') || undefined;
  const inline = all.filter((x) => x.length > META_MAX).join(' · ') || undefined;
  return { meta, inline };
}

const ALERT_STATUS = /^(high|severe|critical|untreated)\b/i;

/** What a row shows after its name. Only data the entry already carries; nothing is added. */
export function railV3RowParts(sectionId: string, entry: IpsEntry, extras?: ChartRowExtras): RailV3RowParts {
  const join = (...v: (string | number | undefined)[]) => v.filter((x) => x !== undefined && x !== '').join(' · ') || undefined;
  const both = (meta: (string | number | undefined)[], inline?: string): RailV3RowParts => {
    const s = splitMeta(...meta);
    return { meta: s.meta, inline: join(s.inline, inline) };
  };
  switch (sectionId) {
    case 'active-problems':
      return both([extras?.onsetYear, entry.code], entry.secondary);
    case 'resolved-problems':
      return both([entry.code, extras?.resolvedOn], entry.secondary);
    case 'medications': {
      const m = /^Stopped(?:\s—\s)?(.*)$/.exec(entry.secondary ?? '');
      if (m) return { ...both([entry.code, m[1] || undefined]), status: { word: 'Stopped', alert: false } };
      // The dose is a short fact for the end slot; a long dose or instruction wraps under the name and never squeezes it.
      return both([entry.secondary, entry.code]);
    }
    case 'procedures':
    case 'vaccinations':
      // Date then code at the row's end ("2004 · 44950", "12 Oct 2025"): the record's own date, from the chart read.
      return both([extras?.date, entry.code], entry.secondary);
    case 'allergies':
      return entry.secondary && ALERT_STATUS.test(entry.secondary)
        ? { status: { word: entry.secondary, alert: true } }
        : { inline: entry.secondary };
    default:
      return both([entry.code], entry.secondary);
  }
}

/** `?x=rail-fixes` (Samer, 3 Nov: "allergy is never written in small … Those are medical terms: Allergy, Problems, Medication"): a
 *  name that comes back all lower case ("allergy to peanuts") reads with its first letter up ("Allergy to peanuts"). Display only:
 *  the stored value is untouched. A name with any capital is left as written ("HbA1c", "JULMENTIN - 250 MG", "Penicillin"). */
export function railV3SentenceCase(text: string): string {
  if (/\p{Lu}/u.test(text)) return text;
  const at = text.search(/\p{L}/u);
  return at < 0 ? text : text.slice(0, at) + text.charAt(at).toLocaleUpperCase() + text.slice(at + 1);
}

/** The sections whose row names are medical terms and read in sentence case with `?x=rail-fixes`. */
export const RAIL_V3_SENTENCE_CASE: readonly string[] = ['allergies', 'active-problems', 'resolved-problems', 'medications'];

/** The kind word on the card's second line ("Problem · I10"). */
const KIND: Readonly<Record<string, string>> = {
  allergies: 'Allergy',
  'active-problems': 'Problem',
  'resolved-problems': 'Resolved problem',
  medications: 'Medication',
  flags: 'Flag',
  procedures: 'Procedure',
  vaccinations: 'Vaccination',
  'family-history': 'Family history',
  'social-history': 'Social history',
};

export interface RailV3CardField {
  label: string;
  value: string;
  strong?: boolean;
}

export interface RailV3CardInfo {
  name: string;
  kind?: string;
  code?: string;
  fields: RailV3CardField[];
}

/** What the card shows about a row: only what the record holds (nothing is invented). */
export function railV3CardInfo(sectionId: string, entry: IpsEntry, extras?: ChartRowExtras): RailV3CardInfo {
  const fields: RailV3CardField[] = [];
  const add = (label: string, value: string | number | undefined, strong?: boolean) => {
    if (value !== undefined && value !== '') fields.push({ label, value: String(value), strong });
  };
  if (sectionId === 'active-problems') {
    add('Status', 'Active', true);
    add('Since', extras?.onsetYear);
    add('Note', entry.secondary);
  } else if (sectionId === 'resolved-problems') {
    add('Status', 'Resolved', true);
    add('Resolved', extras?.resolvedOn);
    add('Note', entry.secondary);
  } else if (sectionId === 'allergies') {
    add('Reaction', entry.secondary);
  } else if (sectionId === 'medications') {
    add('Dose', entry.secondary);
  } else {
    add('Details', entry.secondary);
  }
  return { name: entry.primary, kind: KIND[sectionId], code: entry.code, fields };
}

/** Action order on the bar and in the card. Anything else comes after, in the rail's own order. */
const ACTION_RANK: readonly string[] = ['Add to note', '+ Note', 'Resolve', 'Unresolve', 'Edit'];

export function railV3SortActions(actions: readonly IpsAction[]): IpsAction[] {
  // Planned actions (rail-v3-actions.ts, left-rail target §3) already come in the card's ruled order: kept as given.
  if (actions.length > 0 && actions.every(isRailV3Action)) return [...actions];
  const rank = (label: string) => (ACTION_RANK.includes(label) ? ACTION_RANK.indexOf(label) : ACTION_RANK.length);
  return [...actions].sort((a, b) => rank(a.label) - rank(b.label));
}

/** The words the card and the bar use. "+ Note" is the pre-visit comment on a problem: "Add a note". "Unresolve" is "Reactivate". */
export function railV3ActionWord(label: string): string {
  if (label === '+ Note') return 'Add a note';
  if (label === 'Unresolve') return 'Reactivate';
  return label;
}

/** A nurse has no Resolve (Antonin, 2 Nov). UI only; the handler and the physician's rail are untouched. The medication Stop is
 *  no longer filtered here (left-rail target §3: "never let a look switch remove an action"). */
export function railV3ActionsFor(_model: ChartColumnSection, entry: IpsEntry, opts: { canResolve: boolean }): IpsEntry {
  const shown = entry;
  if (opts.canResolve || !shown.actions?.some((a) => a.label === 'Resolve')) return shown;
  return { ...shown, actions: shown.actions.filter((a) => a.label !== 'Resolve') };
}

/** Where the rail is drawn: before the visit, on the started encounter, or the read-only patient record. */
export type RailV3Stage = 'pre-visit' | 'started' | 'record';

/** The status at a row's end as the catalogue row takes it (left-rail target §5): an alert word is the row's `mark` (glyph + word,
 *  red); a status that is not an alert ("Stopped") has no neutral mark in the catalogue, so it joins the row's numbers. */
export function railV3RowEnd(parts: RailV3RowParts): { meta?: string; mark?: string } {
  if (!parts.status) return { meta: parts.meta };
  if (parts.status.alert) return { meta: parts.meta, mark: parts.status.word };
  return { meta: [parts.meta, parts.status.word].filter(Boolean).join(' · ') };
}
